import type { PlaybackSpec, SoundId } from '@audigoal/core';
import { buildBacking, type Backing, type BackingOptions, type ChartChord } from './backing-track';
import { Metronome } from './metronome';
import { Sampler, type SamplerOptions } from './sampler';
import { Scheduler, type Clock, type Ticker } from './scheduler';
import { beatAt, expandPlayback, type ExpandedPlayback } from './timing';
import { PercussionSynth, UiSynth, type UiSoundId, type UiSoundParams } from './ui-synth';

/**
 * One AudioContext for the whole app (plan doc 10). Music, UI sounds and the
 * metronome each have their own gain so Settings can mix them independently.
 *
 * The context is created lazily inside a user gesture (`ensureStarted`);
 * `installUnlockListeners` keeps resuming it on every gesture until it runs,
 * which is what iOS needs.
 */

export type EngineState = 'idle' | 'starting' | 'running' | 'suspended' | 'unavailable';

export interface PlayOptions {
  onCursor?: (beat: number) => void;
  onEnd?: () => void;
  gain?: number;
  /** Extra sounds to trigger metronome ticks for count-in/overlay. */
  metronomeGain?: number;
}

export interface PlaybackHandle {
  stop(): void;
  readonly isPlaying: boolean;
  /** Seconds the playback lasts, including count-in. */
  readonly totalSeconds: number;
}

export interface EngineOptions {
  /** For tests: a ready-made context. */
  context?: AudioContext;
  createContext?: () => AudioContext;
  sampler?: SamplerOptions;
  makeTicker?: () => Ticker;
  /** requestAnimationFrame stand-in for cursor callbacks. */
  raf?: (cb: () => void) => void;
}

type Unsubscribe = () => void;

export class AudioEngine {
  private static instance: AudioEngine | null = null;

  static get(): AudioEngine {
    if (AudioEngine.instance === null) AudioEngine.instance = new AudioEngine();
    return AudioEngine.instance;
  }

  /** Tests create isolated engines instead of the singleton. */
  static create(options: EngineOptions): AudioEngine {
    return new AudioEngine(options);
  }

  static __resetForTests(): void {
    AudioEngine.instance?.dispose();
    AudioEngine.instance = null;
  }

  private ctx: AudioContext | null = null;
  private stateValue: EngineState = 'idle';
  private listeners = new Set<(state: EngineState) => void>();
  private playListeners = new Set<(seconds: number) => void>();
  private starting: Promise<void> | null = null;

  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private uiGain: GainNode | null = null;
  private clickGain: GainNode | null = null;
  private samplerInstance: Sampler | null = null;
  private uiSynth: UiSynth | null = null;
  private metronomeInstance: Metronome | null = null;
  private muted = false;
  private volumes = { music: 1, ui: 0.6, click: 0.8 };
  private measuredLatency: number | undefined;
  private unlockCleanup: (() => void) | null = null;
  private activeHandles = new Set<{ stop(): void }>();

  private constructor(private readonly options: EngineOptions = {}) {
    if (options.context !== undefined) this.attach(options.context);
  }

  /* ---------------------------------------------------------------- state */

  get state(): EngineState {
    return this.stateValue;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  onStateChange(callback: (state: EngineState) => void): Unsubscribe {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private setState(state: EngineState): void {
    if (this.stateValue === state) return;
    this.stateValue = state;
    for (const l of this.listeners) l(state);
  }

  /**
   * Fires whenever the app itself makes a pitched sound, with how long it will
   * last. The microphone provider listens so the detector ignores that
   * stretch: a lesson that asks for the note it has just played would
   * otherwise hear itself through the phone's speaker and mark the exercise
   * passed before the learner had touched a string.
   */
  onPlay(callback: (seconds: number) => void): Unsubscribe {
    this.playListeners.add(callback);
    return () => this.playListeners.delete(callback);
  }

  private announce(seconds: number): void {
    for (const l of this.playListeners) l(seconds);
  }

  /** Creates or resumes the context. Call from a user gesture. */
  async ensureStarted(): Promise<void> {
    if (this.stateValue === 'running') return;
    if (this.starting !== null) return this.starting;
    this.starting = this.startInner().finally(() => {
      this.starting = null;
    });
    return this.starting;
  }

  private async startInner(): Promise<void> {
    if (this.ctx === null) {
      const factory =
        this.options.createContext ??
        (() => {
          const Ctor =
            (
              globalThis as {
                AudioContext?: typeof AudioContext;
                webkitAudioContext?: typeof AudioContext;
              }
            ).AudioContext ??
            (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (Ctor === undefined) throw new Error('Web Audio is not available');
          return new Ctor({ latencyHint: 'interactive' });
        });
      try {
        this.setState('starting');
        this.attach(factory());
      } catch {
        this.setState('unavailable');
        return;
      }
    }
    const ctx = this.ctx as AudioContext;
    try {
      if (ctx.state !== 'running') await ctx.resume();
      // A silent buffer unlocks output on iOS.
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch {
      // resume() rejects when not in a gesture; the listeners will retry.
    }
    this.setState(ctx.state === 'running' ? 'running' : 'suspended');
  }

  private attach(ctx: AudioContext): void {
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.connect(ctx.destination);
    this.musicGain = ctx.createGain();
    this.uiGain = ctx.createGain();
    this.clickGain = ctx.createGain();
    this.musicGain.connect(this.master);
    this.uiGain.connect(this.master);
    this.clickGain.connect(this.master);
    this.applyVolumes();
    this.samplerInstance = new Sampler(ctx, this.musicGain, this.options.sampler ?? {});
    this.uiSynth = new UiSynth(ctx, this.uiGain);
    const clock: Clock = { now: () => ctx.currentTime };
    this.metronomeInstance = new Metronome(
      clock,
      new PercussionSynth(ctx, this.clickGain),
      this.options.makeTicker,
    );
    const onChange = (): void => {
      if (this.ctx === null) return;
      this.setState(
        this.ctx.state === 'running'
          ? 'running'
          : this.ctx.state === 'closed'
            ? 'unavailable'
            : 'suspended',
      );
    };
    if (typeof ctx.addEventListener === 'function') ctx.addEventListener('statechange', onChange);
    this.setState(ctx.state === 'running' ? 'running' : 'suspended');
  }

  /**
   * Resumes on every gesture until running, and again when the page becomes
   * visible. Returns a cleanup function.
   */
  installUnlockListeners(
    target: Pick<Document, 'addEventListener' | 'removeEventListener'> | null = typeof document ===
    'undefined'
      ? null
      : document,
  ): Unsubscribe {
    if (target === null) return () => {};
    this.unlockCleanup?.();
    const onGesture = (): void => {
      void this.ensureStarted();
    };
    const onVisible = (): void => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible' &&
        this.ctx !== null
      )
        void this.ensureStarted();
    };
    // `pointerup`, not `pointerdown`: resuming the context re-renders the app,
    // and a DOM change between press and release loses the click entirely.
    target.addEventListener('pointerup', onGesture, { passive: true });
    target.addEventListener('keyup', onGesture);
    target.addEventListener('visibilitychange', onVisible);
    this.unlockCleanup = () => {
      target.removeEventListener('pointerup', onGesture);
      target.removeEventListener('keyup', onGesture);
      target.removeEventListener('visibilitychange', onVisible);
    };
    return this.unlockCleanup;
  }

  /* --------------------------------------------------------------- mixing */

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolumes();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setVolume(channel: 'music' | 'ui' | 'click', volume: number): void {
    this.volumes[channel] = Math.max(0, Math.min(1, volume));
    this.applyVolumes();
  }

  getVolume(channel: 'music' | 'ui' | 'click'): number {
    return this.volumes[channel];
  }

  private applyVolumes(): void {
    if (this.ctx === null || this.master === null) return;
    const t = this.ctx.currentTime;
    this.master.gain.setValueAtTime(this.muted ? 0 : 1, t);
    this.musicGain?.gain.setValueAtTime(this.volumes.music, t);
    this.uiGain?.gain.setValueAtTime(this.volumes.ui * 0.5, t);
    this.clickGain?.gain.setValueAtTime(this.volumes.click, t);
  }

  /** Seconds on the audio clock, the same one detection timestamps frames with. */
  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  latency(): { base: number; output: number; measured?: number } {
    const base = this.ctx?.baseLatency ?? 0;
    const output = (this.ctx as { outputLatency?: number } | null)?.outputLatency ?? 0;
    return {
      base,
      output,
      ...(this.measuredLatency !== undefined ? { measured: this.measuredLatency } : {}),
    };
  }

  setMeasuredLatency(seconds: number | undefined): void {
    this.measuredLatency = seconds;
  }

  /* ------------------------------------------------------------- playing */

  get sampler(): Sampler {
    if (this.samplerInstance === null) throw new Error('AudioEngine has not started');
    return this.samplerInstance;
  }

  get metronome(): Metronome {
    if (this.metronomeInstance === null) throw new Error('AudioEngine has not started');
    return this.metronomeInstance;
  }

  get isReady(): boolean {
    return this.ctx !== null && this.samplerInstance !== null;
  }

  async preload(sounds: readonly SoundId[]): Promise<void> {
    if (this.samplerInstance === null) return;
    await Promise.all(sounds.map((s) => this.samplerInstance!.load(s)));
  }

  /** Plays a note right away, for widget taps. */
  playNote(
    sound: SoundId,
    midi: number,
    options: { dur?: number; vel?: number; at?: number } = {},
  ): void {
    if (this.samplerInstance === null || this.ctx === null) return;
    const time = options.at ?? this.ctx.currentTime;
    const duration = options.dur ?? 1;
    this.announce(Math.max(0, time - this.ctx.currentTime) + duration);
    this.samplerInstance.play(sound, midi, {
      time,
      duration,
      velocity: options.vel ?? 0.8,
    });
  }

  strum(
    sound: SoundId,
    midis: readonly number[],
    options: { dir?: 'down' | 'up'; spreadMs?: number; dur?: number } = {},
  ): void {
    if (this.ctx === null) return;
    const dir = options.dir ?? 'down';
    const spread = (options.spreadMs ?? (dir === 'down' ? 18 : 14)) / 1000;
    const ordered = dir === 'down' ? [...midis] : [...midis].reverse();
    const now = this.ctx.currentTime;
    ordered.forEach((midi, i) =>
      this.playNote(sound, midi, { at: now + i * spread, dur: options.dur ?? 1.5 }),
    );
  }

  ui(sound: UiSoundId, params?: UiSoundParams): void {
    if (this.uiSynth === null || this.stateValue !== 'running') return;
    this.uiSynth.play(sound, params);
  }

  play(spec: PlaybackSpec, options: PlayOptions = {}): PlaybackHandle {
    const ctx = this.ctx;
    const sampler = this.samplerInstance;
    if (ctx === null || sampler === null) {
      return { stop: () => {}, isPlaying: false, totalSeconds: 0 };
    }
    const expanded = expandPlayback(spec);
    this.announce(0.08 + expanded.totalSeconds);
    const startAt = ctx.currentTime + 0.08;
    const clock: Clock = { now: () => ctx.currentTime };
    const percussion = new PercussionSynth(ctx, this.clickGain as GainNode);
    let playing = true;
    let cursorRunning = options.onCursor !== undefined;
    const handle = {
      stop: () => {
        if (!playing) return;
        playing = false;
        cursorRunning = false;
        scheduler.stop();
        sampler.stopAll();
        this.activeHandles.delete(handle);
        options.onEnd?.();
      },
      get isPlaying() {
        return playing;
      },
      totalSeconds: expanded.totalSeconds,
    };
    const scheduler = new Scheduler(clock, {
      ...(this.options.makeTicker !== undefined ? { ticker: this.options.makeTicker() } : {}),
      onDone: () => {
        if (spec.loop === true && playing) {
          schedule(clock.now());
          scheduler.start();
          return;
        }
        playing = false;
        cursorRunning = false;
        this.activeHandles.delete(handle);
        options.onEnd?.();
      },
    });
    const gain = options.gain ?? 1;
    const schedule = (base: number): void => {
      const events = [
        ...expanded.notes.map((n) => ({
          time: base + n.time,
          fire: (t: number) => {
            sampler.play(n.sound, n.midi, {
              time: t,
              duration: n.duration,
              velocity: n.velocity * gain,
              ...(n.detuneCents !== undefined && n.detuneCents !== 0
                ? { detuneCents: n.detuneCents }
                : {}),
            });
          },
        })),
        ...expanded.ticks.map((tick) => ({
          time: base + tick.time,
          fire: (t: number) => percussion.click(t, tick.accent ? 'accent' : 'normal'),
        })),
      ];
      scheduler.add(events, base + expanded.totalSeconds);
    };
    schedule(startAt);
    scheduler.start();
    this.activeHandles.add(handle);

    if (options.onCursor !== undefined) {
      const raf =
        this.options.raf ??
        ((cb: () => void) =>
          typeof requestAnimationFrame === 'function'
            ? requestAnimationFrame(cb)
            : setTimeout(cb, 16));
      const onCursor = options.onCursor;
      const step = (): void => {
        if (!cursorRunning) return;
        const elapsed = ctx.currentTime - startAt;
        if (elapsed >= 0) onCursor(beatAt(elapsed, expanded));
        raf(step);
      };
      raf(step);
    }
    return handle;
  }

  stopAll(): void {
    for (const h of [...this.activeHandles]) h.stop();
    this.metronomeInstance?.stop();
  }

  /* -------------------------------------------------------------- backing */

  buildBacking(chart: readonly ChartChord[], options: BackingOptions): Backing {
    return buildBacking(chart, options);
  }

  playBacking(
    backing: Backing,
    options: {
      mute?: Backing['tracks'][number]['id'][];
      onEnd?: () => void;
      onCursor?: (beat: number) => void;
    } = {},
  ): PlaybackHandle {
    const handles = backing.tracks
      .filter((t) => !(options.mute ?? []).includes(t.id))
      .map((t, i) =>
        this.play(t.spec, {
          gain: t.gain,
          ...(i === 0 && options.onCursor !== undefined ? { onCursor: options.onCursor } : {}),
          ...(i === 0 && options.onEnd !== undefined ? { onEnd: options.onEnd } : {}),
        }),
      );
    return {
      stop: () => handles.forEach((h) => h.stop()),
      get isPlaying() {
        return handles.some((h) => h.isPlaying);
      },
      totalSeconds: Math.max(0, ...handles.map((h) => h.totalSeconds)),
    };
  }

  /** Expands a spec without playing it, for tests and visualizers. */
  static expand(spec: PlaybackSpec): ExpandedPlayback {
    return expandPlayback(spec);
  }

  dispose(): void {
    this.stopAll();
    this.unlockCleanup?.();
    this.samplerInstance?.dispose();
    this.samplerInstance = null;
    this.uiSynth = null;
    this.metronomeInstance = null;
    if (this.ctx !== null && typeof this.ctx.close === 'function')
      void this.ctx.close().catch(() => {});
    this.ctx = null;
    this.setState('idle');
  }
}
